import debounce from 'lodash.debounce'
import React, { useEffect } from 'react'
import ProductCard from '../../search-spring/components/ProductCard'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'
import { Result } from '../../search-spring/types/types'
import { transformShopifyProduct } from '../../helpers/transformShopifyProduct'

type Props = {
  products: Result[]
}

type DisrupterData = {
  button_alignment: string
  button_text: string
  button_url: string
  button_position: string
  desktop_image: string
  heading: string
  middle_text: string
  middle_small_text: string
  mobile_image: string
  subheading: string
  tile_position: number
  video_url: string
  height?: string
  width?: string
  product: string | null
  vertical_alignment?: string
  horizontal_alignment?: string
  bleed?: string
}

declare global {
  interface Window {
    collectionDisrupterData1: DisrupterData
    collectionDisrupterData2: DisrupterData
    collectionDisrupterData3: DisrupterData
    collectionDisrupterDesktopImage1: string
    collectionDisrupterMobileImage1: string
    collectionDisrupterDesktopImage2: string
    collectionDisrupterMobileImage2: string
    collectionDisrupterDesktopImage3: string
    collectionDisrupterMobileImage3: string
  }
}

const CollectionSearchGrid = ({ products }: Props) => {
  if (!products || products.length === 0) return null

  const windowSize = useWindowSize()
  const isMobile = windowSize.width <= 641

  useEffect(() => {
    setTimeout(() => {
      const keySessionStorage = `scrollPos-${CD_STATE.searchspring.searchTerms}`
      window.addEventListener(
        'scroll',
        debounce(() => {
          sessionStorage.setItem(keySessionStorage, window.scrollY.toString())
        }, 200)
      )
      const scrollPos = parseInt(sessionStorage.getItem(keySessionStorage) || '0')

      let index = 0
      const interval = setInterval(() => {
        const scrollHeight = document.documentElement.scrollHeight
        if ((scrollPos && scrollPos < scrollHeight) || index > 5) {
          window.scrollTo({
            left: 0,
            top: scrollPos
          })
          clearInterval(interval)
        } else {
          clearInterval(interval)
        }
        index++
      }, 200)
    }, 500)
  }, [])
  const renderDisrupterTile = (data: DisrupterData, imageUrl: string, index: number) => {
    const videoUrl = data?.video_url
    const isVideoMp4 = videoUrl ? videoUrl.toLowerCase().endsWith('.mp4') : false
    const rootUrlWithoutSlash = window.routes.rootUrlWithoutSlash
    const buttonUrl = data?.button_url
    const cardProduct = data?.product
    const ssCardProduct = cardProduct ? transformShopifyProduct(cardProduct) : null
    const width = ssCardProduct ? '100%' : (data?.width ?? '50%').toLowerCase()
    const height = ssCardProduct ? 'screen' : (data?.height ?? 'screen').toLowerCase()
    const domain = window.routes.domain 
    const buttonUrlWithLocale = !rootUrlWithoutSlash || !buttonUrl
      ? buttonUrl ?? ''
      : buttonUrl.includes(rootUrlWithoutSlash)
      ? buttonUrl
      : buttonUrl.replace(domain, domain + rootUrlWithoutSlash)

    const mediaClass =
      height === 'card' && width === '50%' ? 'lg-max:aspect-[690/490]' :
      height === 'card' && width === '100%' ? 'aspect-[340/212] lg:aspect-[1340/419]' :
      height === 'screen' && width === '100%' ? '' :
      height === 'screen' && width === '50%' ? '' : ''

    const verticalAlignment = (data?.vertical_alignment ?? "space between").toLowerCase()
    const horizontalAlignment = (data?.horizontal_alignment ?? "center").toLowerCase()
    const bleed = (data?.bleed ?? "1").toLowerCase();
    const tileClassName = `
        ${height === 'screen' ? 'row-span-4' : 'row-span-1' }
        ${width === '100%' ? 'md:col-span-4 col-span-2' : 'col-span-2' }
        ${width === '100%' && height === 'card' ? 'aspect-[340/212] lg:aspect-[1340/419] h-auto' : 'h-full' }
        ${horizontalAlignment === 'left' ? '' : 'md:pb-0' }
        ${bleed === 'full' ? 'mx-[-7.5px] md:mx-[-10px]' : '' }
        ${bleed === 'left' ? 'mx-[-7.5px] md:ml-[-10px] md:mr-0' : '' }
        ${bleed === 'right' ? 'mx-[-7.5px] md:mr-[-10px] md:ml-0' : '' }
        ${bleed === 'full mobile only' ? 'mx-[-7.5px] md:mx-0' : '' }
      `
    const tileContent = (
      <div className={`
          disrupter-tile-container
          ${ssCardProduct ? 'disrupter-tile-container--with-product' : '' }
          ${horizontalAlignment === 'left' ? 'mt-0' : '' }
          ${horizontalAlignment === 'left' && width === '50%' && height === 'card' ? 'static h-full' : '' }
        `}>
        {ssCardProduct && (
          <div className='disrupter-tile-product-wrapper'>
            <h4 className='disrupter-tile-product-title'>{ssCardProduct.name}</h4>
            <div className='disrupter-tile-product'>
              <ProductCard product={ssCardProduct} isShowWishlist={false} isShowPrice={false} isShowTitle={false} disableMobileImageSlider />
            </div>
            <a href={ssCardProduct.url} className='Button Button--quaternary-dark'>LEARN MORE</a>
          </div>
        )}
        <div className={`relative ${ height === 'screen' ? 'disrupter-tile-inner' : 'h-full'}`}>
          <div className="h-full">
            <div className="absolute z-10 inset-0 gradient-overlay"></div>
            {videoUrl && isVideoMp4 ? (
              <div className={`overflow-hidden w-full h-full ${mediaClass}`}>
                <video width="100%" height="100%" className="object-cover h-full w-full" playsInline autoPlay={true} loop muted>
                  <source src={videoUrl} type="video/mp4" />
                </video>
              </div>
            ) : (
              <img src={imageUrl} alt="disrupter tile" className={`w-full h-full object-cover ${mediaClass}`} />
            )}
          </div>
          <div className={`disrupter-tile-content ${horizontalAlignment === 'left' ? 'text-left justify-start max-w-full p-[15px] lg:py-[20px] lg:px-[10px] top-0 flex flex-col' : 'text-center justify-center' }`}>
            {(data?.subheading || data?.heading) && <div className={`flex flex-col ${horizontalAlignment === 'left' ? 'items-start mb-[10px]' : 'items-center mb-3'} ${verticalAlignment === 'space between' ? '' : 'justify-end' }`}>
              {data?.subheading && <div className="font-bstSpyre uppercase text-sm text-white mb-2XS">{data.subheading}</div>}
              {data?.heading && <div className={`${horizontalAlignment === 'left' ? 'h5' : 'font-bstSpyre uppercase text-base md:text-xl'} text-white`}>{data.heading}</div>}
            </div>}

            <div className={`flex flex-col justify-center ${horizontalAlignment === 'left' ? 'gap-[5px]' : ' gap-3'}`}>
              {data?.middle_text && data?.button_position !== 'Center' && (<div
                className={`${horizontalAlignment === 'left' ? 'h3' : 'font-bstSpyre text-5xl md:text-6xl'} text-white`}
                dangerouslySetInnerHTML={{ __html: data.middle_text }}
              ></div>)}
              {data?.middle_small_text && (data?.button_position !== 'Center' || horizontalAlignment === 'left') && 
              <div className={`${horizontalAlignment === 'left' ? 'utility-2 w-1/2' : 'font-bstSpyre text-sm'} text-white`}>{data.middle_small_text}</div>}
            </div>

            {buttonUrlWithLocale && data?.button_text && (
              <div
                style={{ gridRow: data?.button_position === 'Center' ? 2 : 3 }}
                className={`flex flex-col ${horizontalAlignment === 'left' ? 'items-start mt-[10px] lg:mt-[15px]' : 'items-center mt-8'} w-full  ${verticalAlignment === 'space between' ? 'justify-end' : '' }`}
              >
                <span
                  className={`
                    h-min text-white uppercase
                    ${horizontalAlignment === 'left'
                      ? 'relative font-calibre text-[12px] font-light tracking-[1.12px] leading-[120%] after:content-[""] after:absolute after:left-0 after:bottom-[-2px] after:w-full after:h-[0.5px] after:bg-current after:transition-opacity after:duration-200 hover:after:opacity-0'
                      : 'disrupter-button backdrop-blur-[25px] bg-opacity-50 font-calibre text-sm-b min-w-[150px] rounded-sm p-2.5 lg:p-SM'}
                  `}
                >
                  {data.button_text}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    )

    return buttonUrlWithLocale ? (
      <a key={index} href={buttonUrlWithLocale} className={tileClassName}>
        {tileContent}
      </a>
    ) : (
      <div key={index} className={tileClassName}>
        {tileContent}
      </div>
    )
  }

  const GridHandler = (): JSX.Element => {
    const disrupterData1 = CD_STATE?.collectionDisrupter?.collectionDisrupterData1
    const disrupterProductCard1 = CD_STATE?.collectionDisrupter?.collectionDisrupterProductCard1
    if (disrupterData1) {
      disrupterData1.product = disrupterProductCard1
    }
    const disrupterData2 = CD_STATE?.collectionDisrupter?.collectionDisrupterData2
    const disrupterProductCard2 = CD_STATE?.collectionDisrupter?.collectionDisrupterProductCard2
    if (disrupterData2) {
      disrupterData2.product = disrupterProductCard2
    }
    const disrupterData3 = CD_STATE?.collectionDisrupter?.collectionDisrupterData3
    const disrupterProductCard3 = CD_STATE?.collectionDisrupter?.collectionDisrupterProductCard3
    if (disrupterData3) {
      disrupterData3.product = disrupterProductCard3
    }

    return (
      <div className="grid custom-grid-styling auto-rows-auto gap-[5px] md:gap-y-3 px-[7.5px] md:px-[10px] mx-auto h-full relative overflow-visible">
        {products.map((product: Result, index: number) => {
          const tilePosition1 = isMobile && disrupterData1?.tile_position_mobile ? disrupterData1?.tile_position_mobile : disrupterData1?.tile_position
          const tilePosition2 = isMobile && disrupterData2?.tile_position_mobile ? disrupterData2?.tile_position_mobile : disrupterData2?.tile_position
          const tilePosition3 = isMobile && disrupterData3?.tile_position_mobile ? disrupterData3?.tile_position_mobile : disrupterData3?.tile_position

          const disrupterTile =
            index === tilePosition1
              ? renderDisrupterTile(
                  disrupterData1,
                  isMobile ? CD_STATE.collectionDisrupter.collectionDisrupterMobileImage1 : CD_STATE.collectionDisrupter.collectionDisrupterDesktopImage1,
                  index
                )
              : index === tilePosition2
              ? renderDisrupterTile(
                  disrupterData2,
                  isMobile ? CD_STATE.collectionDisrupter.collectionDisrupterMobileImage2 : CD_STATE.collectionDisrupter.collectionDisrupterDesktopImage2,
                  index
                )
              : index === tilePosition3
              ? renderDisrupterTile(
                  disrupterData3,
                  isMobile ? CD_STATE.collectionDisrupter.collectionDisrupterMobileImage3 : CD_STATE.collectionDisrupter.collectionDisrupterDesktopImage3,
                  index
                )
              : null

          return (
            <React.Fragment key={product.id}>
              {disrupterTile}
              <div className="w-full">
                <ProductCard product={product} />
              </div>
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  return <GridHandler />
}

export default CollectionSearchGrid
